import { Injectable } from '@angular/core';
import { KalDialogConfig, KalDialogService } from '@kalidea/kaligraphi';
import { Store } from '@ngxs/store';
import gql from 'graphql-tag';
import { Observable, of } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { RouteActivityTypeFrom } from 'src/app/_core/services/activity-navigation.service';
import { GraphqlService } from 'src/app/_core/services/graphql.service';
import { LayoutNavigationExtra, LayoutService } from 'src/app/_core/services/layout.service';
import { ListingComponent } from 'src/app/_shared/components/listing/listing.component';
import {
    ConfirmDialogComponent,
    ConfirmDialogData
} from 'src/app/_shared/dialogs/confirm-dialog/confirm-dialog.component';
import { UserState } from 'src/app/_user/store/user/user.state';
import { RouteActivityType } from 'src/app/board/activity/activity.constants';
import { ActivitySaleFragment } from 'src/app/board/activity/fragments/activity-sale.fragment';
import {
    GetBenefModelForVentesClientAction
} from 'src/app/board/activity/stores/activity-beneficiary/activity-beneficiary.action';
import {
    ActivityInitializeSaleByIdOrderAction,
    ActivitySalesGetDepositsListAction,
    ActivitySetActivityIdAction,
    ActivitySetFiltersAction
} from 'src/app/board/activity/stores/activity-sales/activity-sales.action';
import { ActivitySalesState } from 'src/app/board/activity/stores/activity-sales/activity-sales.state';
import {
    CriteresArticleDtoInput,
    InitModeleForVente,
    MutationInitialisationForInscriptionGrpArgs,
    MutationInitialisationForVentesClientsArgs,
    VentesClientsModeleDto
} from 'src/graphql/generated-types';

interface ActivityNewSaleDetailConfig {
    activityId: number;
    beneficiaryId: number;
    orderId: number;
    activityTypeFrom: RouteActivityTypeFrom;
    isFromOrder?: boolean;
    keepListingFilters?: boolean;
    filters?: CriteresArticleDtoInput;
}

export interface ActivityNewSaleInitialisationInformations {
    activityId: number;
    beneficiaryId: number;
    productId: number;
    articleId: number;
}

@Injectable({
    providedIn: 'root'
})
export class ActivityNewSaleService {

    constructor(private readonly graphqlService: GraphqlService,
                private readonly store: Store,
                private readonly layoutService: LayoutService,
                private readonly dialogService: KalDialogService) {
    }

    initSales(params: {activityId: number}): Observable<VentesClientsModeleDto> {
        return this.graphqlService.mutateOne<'InitialisationForVentesClients', MutationInitialisationForVentesClientsArgs>({
            mutation: gql`
                mutation InitialisationForVentesClients($contexte: ContextInfosInput, $initModel: InitModeleForVenteInput){
                    InitialisationForVentesClients(
                        contexte: $contexte
                        initModel: $initModel
                    ){
                        ...ventesClientModel
                    }
                }
                ${ActivitySaleFragment.ventesClientModelFragment}
            `,
            variables: {
                initModel: this.initModel(params)
            }
        });
    }

    initGroups(params: {activityId: number, productId: number}): Observable<VentesClientsModeleDto> {
        return this.graphqlService.mutateOne<'InitialisationForInscriptionGRP', MutationInitialisationForInscriptionGrpArgs>({
            mutation: gql`
                mutation InitialisationForInscriptionGRP(
                    $contexte: ContextInfosInput
                    $initModel: InitModeleForVenteInput
                ) {
                    InitialisationForInscriptionGRP(
                        contexte: $contexte
                        initModel: $initModel
                    ) {
                        ...ventesClientModel
                    }
                }
                ${ActivitySaleFragment.ventesClientModelFragment}
            `,
            variables: {
                initModel: this.initModel(params)
            }
        });
    }

    initRentals(params: { activityId: number, articleId: number }): Observable<VentesClientsModeleDto> {
        return this.graphqlService.mutateOne<'InitialisationForInscriptionGRP', MutationInitialisationForInscriptionGrpArgs>({
            mutation: gql`
                mutation InitialisationByListIdArticleAndIdBenef(
                    $contexte: ContextInfosInput,
                    $initModel: InitModeleForVenteInput
                ) {
                    InitialisationByListIdArticleAndIdBenef(
                        contexte: $contexte,
                        initModel: $initModel
                    ) {
                        ...ventesClientModel
                    }
                }
                ${ActivitySaleFragment.ventesClientModelFragment}
            `,
            variables: {
                initModel: this.initModel(params)
            }
        });
    }

    initModel({
                  activityId,
                  orderId,
                  productId,
                  beneficiaryId
              }: { activityId?: number, orderId?: number, productId?: number, beneficiaryId?: number }): InitModeleForVente {
        const context = this.store.selectSnapshot(UserState.userContext);

        return {
            IdBenef: beneficiaryId,
            IdActivite: activityId,
            IdCommande: orderId,
            IdProduit: productId,
            IdCaisse: context.CurrentCaisse,
            IdStock: context.CurrentStock,
            IdPV: context.CurrentPV,
            IdDeleg: context.CurrentCE,
        };
    }

    navigateToNewSaleDetail(params: ActivityNewSaleDetailConfig): Observable<boolean> {
        const {activityId, beneficiaryId, orderId, activityTypeFrom, filters} = params;

        if (params.isFromOrder === undefined) {
            params.isFromOrder = true;
        }

        return this.store.dispatch(new ActivitySetActivityIdAction(activityId)).pipe(
            switchMap(() => this.store.dispatch(new GetBenefModelForVentesClientAction(beneficiaryId, orderId))),
            switchMap(() => this.store.dispatch(new ActivityInitializeSaleByIdOrderAction(orderId, this.store.selectSnapshot(ActivitySalesState.currentActivityId) || activityId))),
            switchMap(() => this.store.dispatch(new ActivitySalesGetDepositsListAction(orderId))),
            switchMap(() => filters ? this.store.dispatch(new ActivitySetFiltersAction(filters)) : of(null)),
            switchMap(() => {
                return this.layoutService.navigate({
                    content: ['orders', 'new'],
                    focus: 'empty'
                }, {
                    primaryRoute: 'content',
                    activityTypeFrom,
                    queryParams: {
                        keep: 'order',
                        fromOrder: !!params.isFromOrder,
                        ...(!!params?.keepListingFilters ? {[ListingComponent.keepFiltersParamName]: '1'} : {})
                    }
                });
            })
        );
    }

    checkOrderLineOptionLabel(orderLineOptionLabel: string): void {
        const orderLineOptionsList = orderLineOptionLabel.split('\r\n');
        const message = [orderLineOptionsList[0]];
        const articlesList: string[] = orderLineOptionsList.slice(1).map(article => article.slice(2));

        articlesList.forEach(article => {
            message.push(article);
        });

        const config = new KalDialogConfig<ConfirmDialogData>({
            title: 'Ajout automatique des options',
            data: {
                paragraphs: message,
                cancelLabel: 'Fermer'
            }
        });

        this.dialogService.open(ConfirmDialogComponent, config);
    }

    /**
     * Surcouche à LayoutService#navigateBack dans le contexte de l'écran de commande
     * pour sauvegarder le contexte lors du retour de navigation si la page qui précède la page actuelle était celle d'une création/modif de commande.
     */
    handleNavigateBackWithSaleContext$(benefId?: number, overrideExtra?: LayoutNavigationExtra): Observable<boolean> {
        let orderId: number | null = null;
        let saleContext: VentesClientsModeleDto | null = null;

        // FWA - Workaround suite à la montée de version NGXS (18) :
        // navigation entre la fiche bénef et l'écran précédent qui n'est pas une activité. Causant un crash
        try {
            orderId = this.store.selectSnapshot(ActivitySalesState.orderId);
            saleContext = this.store.selectSnapshot(ActivitySalesState.saleContext);
        } catch (error) {
            // on prévient quand-même qu'un problème lié à l'instance du state s'est produit
            console.error('Order / sale context checking failed : State not initialized.');
        }

        // Si l'on vient d'une création / modif d'une commande,
        // alors on effectue les tests sur l'id commande et du contexte pour revenir sur ledit écran sans créer de nouvelle commande
        if (orderId && saleContext) {
            // On mappe les propriétés de l'objet VentesClientsModeleDto avec leurs correspondances en tant que RouteActivityType
            const activityTypeFromObj: Record<keyof Pick<VentesClientsModeleDto, 'IsChqBons' | 'IsLocation' | 'IsInscriptionGrp'>, RouteActivityType> = {
                IsChqBons: RouteActivityType.Sales,
                IsLocation: RouteActivityType.Rentals,
                IsInscriptionGrp: RouteActivityType.Groups
            };

            // On récupère le boolean à true parmi les 3 se trouvant dans activityTypeFromObj
            const currentActivityTypeFromBooleanKey = Object.keys(activityTypeFromObj).find(k => !!saleContext[k]);

            const orderHeaderModel = saleContext.EnteteDevisModel || null;
            const beneficiaryId = saleContext.IdBenef || orderHeaderModel?.IdBenef || benefId;

            return this.navigateToNewSaleDetail({
                beneficiaryId,
                orderId,
                activityId: orderHeaderModel?.IdActivite,
                activityTypeFrom: activityTypeFromObj[currentActivityTypeFromBooleanKey], // On récupère la bonne valeur pour le bon contexte (VEN, GRP, LOC..)
                isFromOrder: false
            });
        } else {
            return overrideExtra
                ? this.layoutService.navigateBack(null, overrideExtra)
                : this.layoutService.navigateBack();
        }
    }
}
